import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import type { ContainerListingEntry, ListableContainerType } from '../shared/types';
import { entityDetailHref } from './container-listing';
import {
  buildInventoryHierarchy,
  storageEntityDisplayLabel,
  type ContainerPlacementStatus,
  type InventoryHierarchyNode,
} from './inventory-hierarchy';
import {
  browserStorageHierarchyStateStore,
  readStorageHierarchyExpansion,
  writeStorageHierarchyExpansion,
} from './storage-hierarchy-state';
import { useStorageApi } from './storage-api';

const CONTAINER_TYPES: readonly ListableContainerType[] = [
  'racks',
  'shelves',
  'boxes',
  'rows',
  'batches',
];

type StorageApi = ReturnType<typeof useStorageApi>;

async function loadEveryContainerPage(
  api: StorageApi,
  type: ListableContainerType,
): Promise<ContainerListingEntry[]> {
  const items: ContainerListingEntry[] = [];
  const seen = new Set<string>();
  let page: string | undefined;
  do {
    const result = await api.listContainers(type, page, { limit: 100 });
    if (!result.ok) throw new Error(result.message ?? result.error);
    for (const entry of result.data.items) {
      if (!seen.has(entry.entityId)) {
        seen.add(entry.entityId);
        items.push(entry);
      }
    }
    page = result.data.nextPageToken;
  } while (page !== undefined);
  return items;
}

function severityLabel(status: ContainerPlacementStatus): string {
  switch (status.severity) {
    case 'critical':
      return 'Critical';
    case 'attention':
      return 'Attention';
    case 'awaiting':
      return 'Awaiting putaway';
    case 'placed':
      return 'Placed';
    case 'root':
      return 'Root';
  }
}

function PlacementBadge({ status }: { status: ContainerPlacementStatus }): JSX.Element {
  return (
    <span className={`tcvs-placement-badge tcvs-placement-${status.severity}`}>
      {severityLabel(status)}
    </span>
  );
}

function HierarchyNode({
  node,
  expanded,
  onToggle,
  issueById,
}: {
  node: InventoryHierarchyNode;
  expanded: ReadonlySet<string>;
  onToggle: (entityId: string) => void;
  issueById: ReadonlyMap<string, ContainerPlacementStatus>;
}): JSX.Element {
  const hasChildren = node.children.length > 0;
  const isExpanded = expanded.has(node.entry.entityId);
  const issueStatus = issueById.get(node.entry.entityId);
  return (
    <div className="tcvs-inventory-tree-node">
      <div className="tcvs-inventory-tree-row">
        {hasChildren ? (
          <button
            type="button"
            className="tcvs-inventory-tree-disclosure"
            aria-label={`${isExpanded ? 'Collapse' : 'Expand'} ${storageEntityDisplayLabel(node.entry)}`}
            aria-expanded={isExpanded}
            onClick={() => onToggle(node.entry.entityId)}
          >
            <span aria-hidden="true">{isExpanded ? '▾' : '▸'}</span>
          </button>
        ) : (
          <span className="tcvs-inventory-tree-leaf" aria-hidden="true" />
        )}
        <Link
          className="tcvs-inventory-tree-link"
          to={entityDetailHref(node.entry.entityType, node.entry.entityId)}
        >
          {storageEntityDisplayLabel(node.entry)}
        </Link>
        {node.children.length > 0 ? (
          <span className="tcvs-storage-group-count">{node.children.length}</span>
        ) : null}
        {issueStatus !== undefined ? <PlacementBadge status={issueStatus} /> : null}
      </div>
      {hasChildren && isExpanded ? (
        <div className="tcvs-inventory-tree-children">
          {node.children.map((child) => (
            <HierarchyNode
              key={child.entry.entityId}
              node={child}
              expanded={expanded}
              onToggle={onToggle}
              issueById={issueById}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}

export function InventoryLocationsSection(): JSX.Element {
  const api = useStorageApi();
  const { claims } = useAuth();
  const reviewerId = claims?.sub;
  const loadSequence = useRef(0);
  const [entries, setEntries] = useState<ContainerListingEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showIssuesOnly, setShowIssuesOnly] = useState(false);
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(() =>
    readStorageHierarchyExpansion(
      browserStorageHierarchyStateStore(),
      'inventory-locations',
      reviewerId,
    ),
  );

  const load = useCallback(async () => {
    const sequence = (loadSequence.current += 1);
    setLoading(true);
    setError(null);
    try {
      const pages = await Promise.all(
        CONTAINER_TYPES.map((type) => loadEveryContainerPage(api, type)),
      );
      if (sequence !== loadSequence.current) return;
      setEntries(pages.flat());
    } catch (cause) {
      if (sequence !== loadSequence.current) return;
      setError(cause instanceof Error ? cause.message : String(cause));
    } finally {
      if (sequence === loadSequence.current) setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    void load();
    return () => {
      loadSequence.current += 1;
    };
  }, [load]);

  const model = useMemo(() => buildInventoryHierarchy(entries), [entries]);
  const issueById = useMemo(
    () => new Map(model.issues.map((issue) => [issue.entry.entityId, issue.status])),
    [model.issues],
  );
  const criticalCount = model.issues.filter((issue) => issue.status.severity === 'critical').length;
  const attentionCount = model.issues.length - criticalCount;

  const toggle = useCallback(
    (entityId: string) => {
      setExpanded((current) => {
        const next = new Set(current);
        if (next.has(entityId)) next.delete(entityId);
        else next.add(entityId);
        writeStorageHierarchyExpansion(
          browserStorageHierarchyStateStore(),
          'inventory-locations',
          reviewerId,
          next,
        );
        return next;
      });
    },
    [reviewerId],
  );

  return (
    <div className="tcvs-inventory-locations">
      <section className="tcvs-card">
        <div className="tcvs-inventory-section-heading">
          <div>
            <h3>Storage hierarchy</h3>
            <p className="tcvs-caption">Rack → Shelf → Box → Row → Batch</p>
          </div>
          <div className="tcvs-inventory-hierarchy-actions">
            <label className="tcvs-inventory-issues-filter">
              <input
                type="checkbox"
                checked={showIssuesOnly}
                onChange={(event) => setShowIssuesOnly(event.currentTarget.checked)}
              />
              Placement issues only
            </label>
            <button
              type="button"
              className="tcvs-btn-secondary tcvs-btn-compact"
              onClick={() => void load()}
              disabled={loading}
            >
              Refresh
            </button>
          </div>
        </div>

        <p className="tcvs-placement-summary" role="status">
          <span>{entries.length} containers</span>
          {model.issues.length === 0 ? (
            <strong className="tcvs-tone-stored">No placement issues</strong>
          ) : (
            <>
              <strong className={criticalCount > 0 ? 'tcvs-tone-pulled' : 'tcvs-tone-stored'}>
                {criticalCount} critical
              </strong>
              {attentionCount > 0 ? <span>{attentionCount} awaiting attention</span> : null}
            </>
          )}
        </p>

        {loading && entries.length === 0 ? <p className="tcvs-muted">Checking placement…</p> : null}
        {error !== null ? (
          <p className="tcvs-error">
            Error: {error}{' '}
            <button type="button" className="tcvs-linklike" onClick={() => void load()}>
              Retry
            </button>
          </p>
        ) : null}
        {showIssuesOnly ? (
          model.issues.length > 0 ? (
            <div className="tcvs-placement-issue-list">
              {model.issues.map(({ entry, status }) => (
                <div key={entry.entityId} className="tcvs-placement-issue-row">
                  <PlacementBadge status={status} />
                  <Link to={entityDetailHref(entry.entityType, entry.entityId)}>
                    {storageEntityDisplayLabel(entry)}
                  </Link>
                  <span>{status.label}</span>
                  <Link
                    className="tcvs-linklike tcvs-placement-putaway-link"
                    to={`/storage?section=Putaway&item=${encodeURIComponent(entry.entityId)}`}
                  >
                    Putaway now
                  </Link>
                </div>
              ))}
            </div>
          ) : !loading && error === null ? (
            <p className="tcvs-tone-stored">All container placement links are complete.</p>
          ) : null
        ) : (
          <div className="tcvs-inventory-tree">
            {model.roots.map((node) => (
              <HierarchyNode
                key={node.entry.entityId}
                node={node}
                expanded={expanded}
                onToggle={toggle}
                issueById={issueById}
              />
            ))}
            {model.detachedRoots.length > 0 ? (
              <section className="tcvs-inventory-detached">
                <h4>Unplaced branches</h4>
                {model.detachedRoots.map((node) => (
                  <HierarchyNode
                    key={node.entry.entityId}
                    node={node}
                    expanded={expanded}
                    onToggle={toggle}
                    issueById={issueById}
                  />
                ))}
              </section>
            ) : null}
            {!loading && entries.length === 0 && error === null ? (
              <p className="tcvs-muted">No storage containers have been created.</p>
            ) : null}
          </div>
        )}
      </section>
    </div>
  );
}
